import React, { useState } from "react";
import {
  TextInput,
  SimpleForm,
  required,
  ReferenceInput,
  AutocompleteInput,
  useEditController,
  EditProps,
  FormDataConsumer,
} from "react-admin";
import SanitizedGrid from "components/SanitizedGrid";
import { Grid, Typography } from "@material-ui/core";
import { GetParam } from "utils/tools";
import CustomEditToolbar from "components/CustomEditToolbar";
import CustomMonthPickerInput from "components/CustomMonthPickerInput";
import SubcontractorHolidayPaymentRouteTypeInput from "./components/SubcontractorHolidayPaymentRouteTypeInput";
import SubcontractorHolidayPaymentPricingTypeInput from "./components/SubcontractorHolidayPaymentPricingTypeInput";
import AcademicYearInput from "components/AcademicYearInput";
import AcademicTermInput from "components/AcademicTermInput";
import CcaAcademicYearInput from "components/CcaAcademicYearInput";
import CcaAcademicTermInput from "components/CcaAcademicTermInput";
import EditWrapper from "../../components/EditWrapper";
import PermissionLoading from "../../components/PermissionLoading";

const getInputBasedRouteType = (props: any, type: string, schoolId: number) => {
  switch (type) {
    case "Regular":
    case "academicYears":
      return (
        <>
          <Grid item xs={4}>
            <AcademicYearInput schoolId={schoolId} />
          </Grid>
          <Grid item xs={8}></Grid>

          <Grid item xs={4}>
            <AcademicTermInput isRequired />
          </Grid>
          <Grid item xs={8}></Grid>
        </>
      );
    case "CCA":
    case "ccaAcademicYears":
      return (
        <>
          <Grid item xs={4}>
            <CcaAcademicYearInput schoolId={schoolId} />
          </Grid>
          <Grid item xs={8}></Grid>

          <Grid item xs={4}>
            <CcaAcademicTermInput isRequired />
          </Grid>
          <Grid item xs={8}></Grid>
        </>
      );
  }
};

const SubcontractorHolidayPaymentsEdit = (props: EditProps) => {
  const param = GetParam();

  const [year, setYear] = useState();
  const [month, setMonth] = useState();

  const { record, loading } = useEditController(props);

  const breadcrumb = [
    { title: "Schools", link: `/schools` },
    {
      title: "SubcontractorHolidayPayments",
      link: `/schools/${param.get("schoolId")}/show/6`,
    },
    { title: "Edit", link: "" },
  ];

  const formatRecord = (res: any) => {
    if (res) {
      if (res.routeType === "Regular") {
        res.academicYearId = res.regularRouteJoinPeriodYearId;
        res.academicTermId = res.regularRouteJoinPeriodTermId;
        res.routeType = "academicYears";
      }

      if (res.routeType === "CCA") {
        res.ccaAcademicYearId = res.ccaRouteJoinPeriodYearId;
        res.ccaAcademicTermId = res.ccaRouteJoinPeriodTermId;
        res.routeType = "ccaAcademicYears";
      }

      // Safely handle and convert holidayPeriodMonth and holidayPeriodYear to holidayPeriod
      if (
        typeof res.holidayPeriodMonth === "number" &&
        typeof res.holidayPeriodYear === "number"
      ) {
        // Ensure holidayPeriodMonth is between 1 and 12
        const month =
          res.holidayPeriodMonth >= 1 && res.holidayPeriodMonth <= 12
            ? String(res.holidayPeriodMonth).padStart(2, "0")
            : "01"; // Default to January if out of range

        // Ensure holidayPeriodYear is reasonable (e.g., a four-digit year)
        const year =
          res.holidayPeriodYear > 1000 && res.holidayPeriodYear < 3000
            ? String(res.holidayPeriodYear)
            : new Date().getFullYear().toString(); // Default to the current year if invalid

        // Combine month and year into the desired format
        res.holidayPeriod = `${month}/${year}`;
      } else {
        console.error(
          "Invalid holidayPeriodMonth or holidayPeriodYear:",
          res.holidayPeriodMonth,
          res.holidayPeriodYear,
        );
      }

      return res;
    }
  };

  const transform = (data: any) => {
    data.holidayPeriodMonth = data.month;
    data.holidayPeriodYear = data.year;

    data.routeType = data.routeType === "academicYears" ? "Regular" : "CCA";

    if (data.routeType === "Regular") {
      data.regularRouteJoinPeriodYearId = data.academicYearId;
      data.regularRouteJoinPeriodTermId = data.academicTermId;

      delete data.ccaRouteJoinPeriodYearId;
      delete data.ccaRouteJoinPeriodTermId;
    }

    if (data.routeType === "CCA") {
      data.ccaRouteJoinPeriodYearId = data.ccaAcademicYearId;
      data.ccaRouteJoinPeriodTermId = data.ccaAcademicTermId;

      delete data.regularRouteJoinPeriodYearId;
      delete data.regularRouteJoinPeriodTermId;
    }

    delete data.holidayPeriod;
    delete data.year;
    delete data.month;
    delete data.routeJoinPeriodTermId;
    delete data.academicYearId;
    delete data.academicTermId;
    delete data.ccaAcademicYearId;
    delete data.ccaAcademicTermId;
    delete data.createdAt;
    delete data.updatedAt;

    return data;
  };

  if (loading) {
    return <PermissionLoading />;
  }

  return (
    <>
      <EditWrapper
        breadcrumb={breadcrumb}
        actions={null}
        transform={transform}
        customName={{
          resourceName: "Subcontractor Holiday Payment",
          propertyName: "name",
        }}
        {...props}
      >
        <SimpleForm
          record={formatRecord(record)}
          toolbar={
            <CustomEditToolbar
              redirectLink={`/schools/${param.get("schoolId")}/show/6`}
              isDeleteWithConfirm={true}
            />
          }
          name="manageSubcontractorHolidayPayment"
        >
          <SanitizedGrid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">School</Typography>
            </Grid>
            <Grid item xs={4}>
              <ReferenceInput
                label="Select School"
                source="schoolId"
                reference="schools"
                disabled
              >
                <AutocompleteInput optionText="name" />
              </ReferenceInput>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6">Details</Typography>
            </Grid>

            <Grid item xs={4}>
              <TextInput source="name" label="Name" validate={required()} />
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={4}>
              <SubcontractorHolidayPaymentRouteTypeInput />
            </Grid>
            <Grid item xs={8}></Grid>

            <FormDataConsumer>
              {({ formData }) => {
                //set the initial Year and Month
                setYear(formData.holidayPeriodYear);
                setMonth(formData.holidayPeriodMonth);

                return getInputBasedRouteType(
                  formData,
                  formData.routeType,
                  formData.schoolId,
                );
              }}
            </FormDataConsumer>

            <Grid item xs={4}>
              <CustomMonthPickerInput
                name="holidayPeriod"
                label="Holiday Period"
                year={year}
                month={month}
              />
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={4}>
              <SubcontractorHolidayPaymentPricingTypeInput />
            </Grid>
            <Grid item xs={8}></Grid>
          </SanitizedGrid>
        </SimpleForm>
      </EditWrapper>
    </>
  );
};

export default SubcontractorHolidayPaymentsEdit;
